/**
 * LandIntelligence — Unified "Cadastre / Land Intelligence" section.
 * Combines: cadastre surface, zoning, ownership, restrictions, insights.
 * Auto-fetches when address changes.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Buildings, TreeEvergreen, SpeakerHigh, Warning,
  ShieldCheck, User, MapTrifold, ArrowSquareOut, Info,
} from '@phosphor-icons/react';
import { API_BASE_URL } from '@/config/api';
import {
  cadastreBelongsToQuery,
  isIncompleteCadastreCache,
  nextForcedRefetchGuard,
  tagCadastreWithQuery,
} from '@/utils/cadastreCache';

function Tip({ children, tip, source }) {
  return (
    <span className="group relative cursor-help">
      {children}
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-50 hidden group-hover:block w-44 bg-gray-900 text-[7px] text-gray-200 rounded px-2 py-1.5 shadow-lg leading-tight">
        {tip}
        {source && <span className="block mt-0.5 text-gray-400 italic">Source: {source}</span>}
      </span>
    </span>
  );
}

const fmt = (v) => v?.toLocaleString('de-CH');
const API = API_BASE_URL;

function cantonMeta(canton) {
  if (canton === 'VD') {
    return {
      surface: 'RDPPF Cadastre VD (État de Vaud)',
      ownership: 'Vaud OGC — parcelle (bien-fond)',
      foncierTip: 'Opens an official cadastre / land-registry related document (RDPPF extract or registry portal).',
      foncierSource: 'vd.ch / rdppf.vd.ch',
      buildings: 'Vaud OGC — bâtiments (RCB / MO)',
      zone: 'RDPPF zone affectation synthèse',
    };
  }
  if (canton === 'FR') {
    return {
      surface: 'RDPPF Cadastre FR (maps.fr.ch RdppfSVC)',
      ownership: 'Registre foncier fédéral / BDMO Fribourg',
      foncierTip: 'Opens the official Fribourg land registry public extract (ownership).',
      foncierSource: 'keycloak.fr.ch / registre foncier FR',
      buildings: 'MO-RegBL RegBAFR — map.geo.fr.ch',
      zone: 'RDPPF FR — affectations primaires',
    };
  }
  return {
    surface: 'RDPPF Cadastre GE',
    ownership: 'SITG CAD_PARCELLE_MENSU',
    foncierTip: 'Opens the official Geneva land registry extract. Shows owner names, shares, property regime, servitudes, and mortgage data.',
    foncierSource: 'ge.ch registre foncier',
    buildings: 'TEREXTRAITFONCIER MapServer bâtiments hors-sol',
    zone: 'RDPPF zone affectation synthèse',
  };
}

/** Parse "Période de 1961 à 1970" or "Période avant 1919" → { from, to, age } */
function parseBuildingAge(era) {
  if (!era) return null;
  // Range: "Période de 1961 à 1970"
  const m = era.match(/(\d{4})\s*[àa\-–]\s*(\d{4})/);
  if (m) {
    const from = parseInt(m[1], 10);
    const to = parseInt(m[2], 10);
    const mid = Math.round((from + to) / 2);
    return { from, to, age: new Date().getFullYear() - mid, label: `${from}–${to}` };
  }
  // "avant YYYY" = before year
  const avant = era.match(/avant\s+(\d{4})/i);
  if (avant) {
    const yr = parseInt(avant[1], 10);
    return { from: null, to: yr, age: new Date().getFullYear() - yr, label: `< ${yr}` };
  }
  // "après YYYY" = after year
  const apres = era.match(/apr[eè]s\s+(\d{4})/i);
  if (apres) {
    const yr = parseInt(apres[1], 10);
    return { from: yr, to: null, age: new Date().getFullYear() - yr, label: `> ${yr}` };
  }
  // Single year
  const single = era.match(/(\d{4})/);
  if (single) {
    const yr = parseInt(single[1], 10);
    return { from: yr, to: yr, age: new Date().getFullYear() - yr, label: `${yr}` };
  }
  return null;
}

export function LandIntelligence({
  address, locationData, onSurfaceAvailable, onCadastreCommune, onDataLoaded, cachedData, refreshKey,
}) {
  const [data, setData] = useState(() => {
    if (cadastreBelongsToQuery(address, cachedData)) return cachedData;
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  // Debounce only: last address we already requested (not a keep-cache signal).
  const lastFetched = useRef(
    cadastreBelongsToQuery(address, cachedData) ? address : '',
  );
  const lastRefreshKey = useRef(refreshKey || 0);
  const timerRef = useRef(null);
  const [refetchTick, setRefetchTick] = useState(0);
  const incompleteClearedRef = useRef(false);
  const autoRetriedRef = useRef('');
  /** Address we already forced a stale-cache refetch for — blocks hydrate loops. */
  const forcedRefetchForRef = useRef('');

  // Hydrate from parent cache via shared cadastreBelongsToQuery (tagged query SSOT).
  useEffect(() => {
    if (cachedData === null) {
      lastFetched.current = '';
      incompleteClearedRef.current = false;
      autoRetriedRef.current = '';
      forcedRefetchForRef.current = '';
      if (address && address.length >= 5) setData(null);
      return;
    }
    if (!cachedData) return;
    // Incomplete legacy cache: parcel ids present but surface never loaded (RDPPF soft-fail).
    // Clear parent + force refetch (fetch effect watches refetchTick).
    if (isIncompleteCadastreCache(cachedData)) {
      lastFetched.current = '';
      setData(null);
      if (!incompleteClearedRef.current) {
        incompleteClearedRef.current = true;
        if (onDataLoaded) onDataLoaded(null);
        const next = nextForcedRefetchGuard(forcedRefetchForRef.current, address);
        if (next) {
          forcedRefetchForRef.current = next;
          setRefetchTick((t) => t + 1);
        }
      }
      return;
    }
    incompleteClearedRef.current = false;
    if (cachedData.status !== 'ok') return;

    if (cadastreBelongsToQuery(address, cachedData)) {
      setData(cachedData);
      lastFetched.current = address;
      forcedRefetchForRef.current = '';
      return;
    }
    // Stale cache for a different place — drop and force refetch once per address.
    if (address && address.length >= 5) {
      lastFetched.current = '';
      setData(null);
      const next = nextForcedRefetchGuard(forcedRefetchForRef.current, address);
      if (next) {
        forcedRefetchForRef.current = next;
        setRefetchTick((t) => t + 1);
      }
    }
  }, [cachedData, address]); // eslint-disable-line react-hooks/exhaustive-deps -- onDataLoaded intentionally omitted (stable setter)

  const buildCadastrePayload = useCallback((addr) => {
    const normalizedAddress = locationData?.selectedAddress || addr;
    const payload = { address: normalizedAddress };
    const lat = locationData?.lat;
    const lon = locationData?.lon;
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      payload.lat = lat;
      payload.lon = lon;
    }
    if (locationData?.selectedAddress) {
      payload.selectedAddress = locationData.selectedAddress;
    }
    return payload;
  }, [locationData?.lat, locationData?.lon, locationData?.selectedAddress]);

  const fetchData = useCallback(async (addr) => {
    if (!addr || addr.length < 5) {
      setLoading(false);
      return;
    }
    if (addr === lastFetched.current) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cadastre`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildCadastrePayload(addr)),
      });
      const d = await res.json();
      const tagged = d?.status === 'ok' ? tagCadastreWithQuery(d, addr) : d;
      setData(tagged);
      lastFetched.current = addr;
      forcedRefetchForRef.current = '';
      if (tagged?.status === 'ok') {
        autoRetriedRef.current = '';
        if (onDataLoaded) onDataLoaded(tagged);
        if (tagged.commune && onCadastreCommune) {
          const pcMatch = tagged.address?.match(/\b([1-9]\d{3})\b/);
          onCadastreCommune(tagged.commune, pcMatch ? pcMatch[1] : '');
        }
      } else if (onDataLoaded) {
        // QA-018: clear parent stale parcel when search yields no/unsupported result
        onDataLoaded(null);
        // One automatic retry for retriable registry soft-failures (any address).
        const retriable = Boolean(d?.egrid) || /Refresh Land Data/i.test(d?.message || '');
        if (d.status === 'error' && retriable && autoRetriedRef.current !== addr) {
          autoRetriedRef.current = addr;
          lastFetched.current = '';
          setTimeout(() => setRefetchTick((t) => t + 1), 1200);
        }
      }
    } catch {
      setData({ status: 'error', message: 'Network error' });
      lastFetched.current = addr;
      if (onDataLoaded) onDataLoaded(null);
    }
    setLoading(false);
  }, [buildCadastrePayload]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!address || address.length < 5) {
      setLoading(false);
      return;
    }
    if (address === lastFetched.current) {
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(() => fetchData(address), 600);
    return () => clearTimeout(timerRef.current);
  }, [address, fetchData, refetchTick]);

  // Force refresh when refreshKey changes
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey !== lastRefreshKey.current) {
      lastRefreshKey.current = refreshKey;
      lastFetched.current = '';
      autoRetriedRef.current = '';
      if (address && address.length >= 5) fetchData(address);
    }
  }, [refreshKey, address, fetchData]);

  if (!address || address.length < 5) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded mt-1 overflow-hidden" data-testid="cadastre-loading">
        <div className="px-2 py-1 bg-gray-50 border-b border-gray-100">
          <div className="text-[8px] text-gray-500 font-semibold uppercase flex items-center gap-1 animate-pulse">
            <Buildings weight="bold" className="w-3 h-3" />
            Loading land data...
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.status === 'not_found') {
    return null;
  }
  if (data.status === 'error') {
    const retriable = Boolean(data.egrid) || /Refresh Land Data/i.test(data.message || '');
    return (
      <div className="bg-white border border-gray-200 rounded mt-1 overflow-hidden" data-testid="cadastre-error">
        <div className="px-2 py-1 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-1">
            <Buildings weight="bold" className="w-3 h-3 text-gray-700" />
            <span className="text-[8px] font-bold text-gray-800 uppercase tracking-wide">Land Intelligence</span>
          </div>
        </div>
        <div className="px-2 py-1.5 space-y-1">
          <span className="text-[8px] text-red-500 block">{data.message}</span>
          {retriable && (
            <button
              type="button"
              className="text-[8px] font-semibold text-indigo-700 underline underline-offset-2"
              data-testid="cadastre-error-retry"
              onClick={() => {
                autoRetriedRef.current = '';
                lastFetched.current = '';
                setData(null);
                if (onDataLoaded) onDataLoaded(null);
                setRefetchTick((t) => t + 1);
              }}
            >
              Retry land data
            </button>
          )}
        </div>
      </div>
    );
  }
  if (data.status === 'unsupported') {
    return (
      <div className="bg-white border border-gray-200 rounded mt-1 overflow-hidden" data-testid="cadastre-unsupported">
        <div className="px-2 py-1 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-1">
            <Buildings weight="bold" className="w-3 h-3 text-gray-700" />
            <span className="text-[8px] font-bold text-gray-800 uppercase tracking-wide">Land Intelligence</span>
          </div>
        </div>
        <div className="px-2 py-1.5">
          <span className="text-[8px] text-amber-600">Canton {data.canton} — cadastre supported for Geneva, Vaud and Fribourg only</span>
        </div>
      </div>
    );
  }

  const zones = data.zones ?? [];
  const restrictions = data.restrictions ?? [];
  const ownership = data.ownership ?? {};
  const clearThemes = data.clear_themes ?? [];
  const buildings = data.buildings ?? [];
  const meta = cantonMeta(data.canton);
  const forestRestrictions = restrictions.filter(r => r.type === 'forest' && r.label);
  const noiseRestrictions = restrictions.filter(r => r.type === 'noise' && r.label);
  const setbackRestrictions = restrictions.filter(r => r.type === 'setback' && r.label);
  const otherRestrictions = restrictions.filter(
    (r) => r.label && !['forest', 'noise', 'setback'].includes(r.type),
  );
  const hasBuildableGap = data.buildable_area_m2 > 0 && data.buildable_area_m2 < data.surface_m2;

  return (
    <div className="bg-white border border-gray-200 rounded mt-1 overflow-hidden" data-testid="cadastre-info">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-2 py-1 bg-gray-50 border-b border-gray-100 hover:bg-gray-100 transition-colors"
        data-testid="cadastre-toggle"
      >
        <div className="flex items-center gap-1">
          <Buildings weight="bold" className="w-3 h-3 text-gray-700" />
          <span className="text-[8px] font-bold text-gray-800 uppercase tracking-wide">Land Intelligence</span>
          {data.commune && <span className="text-[8px] text-gray-400">· {data.commune}</span>}
        </div>
        <span className="text-[9px] text-gray-400">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div className="px-2 py-1.5 space-y-1.5" data-testid="cadastre-details">

          {/* ── 1. CORE METRICS ── */}
          <div data-testid="cadastre-core">
            <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Surface & Zoning</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Total surface */}
              <Tip tip="Total registered parcel surface from the land registry." source={meta.surface}>
                <div className="flex items-center gap-0.5">
                  <span className="text-[11px] font-bold tabular-nums text-gray-900" data-testid="cadastre-surface">{fmt(data.surface_m2)}</span>
                  <span className="text-[7px] text-gray-400">m²</span>
                </div>
              </Tip>
              {data.surface_m2 && onSurfaceAvailable && (
                <button
                  onClick={() => onSurfaceAvailable(data.surface_m2)}
                  className="text-[7px] font-semibold text-white bg-gray-800 hover:bg-gray-700 px-1.5 py-0.5 rounded transition-colors"
                  data-testid="cadastre-apply-surface"
                  title="Apply total surface to Land Size"
                >Apply</button>
              )}
              {/* Buildable area */}
              {hasBuildableGap && (
                <>
                  <span className="text-[7px] text-gray-300">|</span>
                  <Tip tip="Buildable area = zones excluding forest, agricultural, protected areas." source="Calculated from RDPPF zones">
                    <div className="flex items-center gap-0.5">
                      <span className="text-[10px] font-bold tabular-nums text-emerald-700" data-testid="cadastre-buildable">{fmt(data.buildable_area_m2)}</span>
                      <span className="text-[7px] text-emerald-500">m² buildable</span>
                    </div>
                  </Tip>
                  {onSurfaceAvailable && (
                    <button
                      onClick={() => onSurfaceAvailable(data.buildable_area_m2)}
                      className="text-[7px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded transition-colors"
                      data-testid="cadastre-apply-buildable"
                      title="Apply buildable area to Land Size"
                    >Apply buildable</button>
                  )}
                </>
              )}
            </div>

            {/* Zone badges */}
            {zones.length > 0 && (
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {zones.map((z, i) => {
                  const isForest = z.name.toLowerCase().includes('bois') || z.name.toLowerCase().includes('forêt');
                  const isAgri = z.name.toLowerCase().includes('agricole');
                  return (
                    <Tip key={i} tip={z.percent != null ? `${z.name}: ${fmt(z.area_m2)} m² (${z.percent}% of parcel)` : z.name} source={meta.zone}>
                      <span className={`text-[7px] font-semibold px-1.5 py-0.5 rounded ${
                        isForest ? 'bg-green-100 text-green-800 border border-green-200' :
                        isAgri ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                        'bg-blue-50 text-blue-800 border border-blue-200'
                      }`} data-testid={`cadastre-zone-${i}`}>
                        {z.percent != null ? `${z.name} · ${z.percent}%` : z.name}
                      </span>
                    </Tip>
                  );
                })}
              </div>
            )}
            {data.rdppf_partial && zones.length === 0 && (
              <div className="mt-1 text-[7px] text-amber-700" data-testid="cadastre-zoning-partial">
                Zoning not loaded from RDPPF — surface from SITG. Use Refresh Land Data to retry zoning.
              </div>
            )}

            {/* Market Price removed — now rendered independently in InputPanel */}
          </div>

          {/* ── 2. OWNERSHIP ── */}
          <div data-testid="cadastre-ownership">
            <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Ownership</div>
            {ownership.ownership_type ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Tip tip="Property ownership classification: privé (private), public, mixte (mixed), dépendance (dependency/ancillary); or land registry category." source={meta.ownership}>
                  <div className="flex items-center gap-0.5">
                    <User weight="bold" className="w-2.5 h-2.5 text-gray-500" />
                    <span className="text-[9px] font-semibold text-gray-800 capitalize">{ownership.ownership_type}</span>
                  </div>
                </Tip>
                {ownership.validity ? (
                <Tip tip="Current legal validity status of this parcel record." source="SITG parcel registry">
                  <span className={`text-[7px] font-semibold px-1.5 py-0.5 rounded ${
                    ownership.validity === 'en vigueur'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}>{ownership.validity}</span>
                </Tip>
                ) : null}
              </div>
            ) : null}
            {data.foncier_url && (
              <Tip tip={meta.foncierTip} source={meta.foncierSource}>
                <a href={data.foncier_url} target="_blank" rel="noreferrer"
                  className="mt-0.5 flex items-center gap-1 text-[8px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-1 rounded transition-colors w-fit"
                  data-testid="cadastre-foncier-link">
                  <ArrowSquareOut weight="bold" className="w-3 h-3" />
                  View owners & registry details
                </a>
              </Tip>
            )}
          </div>

          {/* ── 3. PARCEL DATA ── */}
          <div data-testid="cadastre-parcel">
            <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Parcel</div>
            <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 text-[8px]">
              <Tip tip="Unique parcel number in the commune's land registry." source="RDPPF cadastre">
                <div><span className="text-gray-400">No:</span> <span className="font-semibold text-gray-700">#{data.parcel_number}</span></div>
              </Tip>
              <Tip tip="EGRID = unique Swiss federal land registry identifier." source="geo.admin.ch / RDPPF">
                <div><span className="text-gray-400">EGRID:</span> <span className="font-mono text-gray-600 text-[7px]">{data.egrid}</span></div>
              </Tip>
              <Tip tip="Commune name and OFS / IdentDN code from the RDPPF extract." source="RDPPF">
                <div><span className="text-gray-400">Commune:</span> <span className="font-semibold text-gray-700">{data.commune}{data.commune_code ? ` (${data.commune_code})` : ''}</span></div>
              </Tip>
            </div>
          </div>

          {/* ── 3b. BUILDINGS ── */}
          {buildings.length > 0 && (
            <div data-testid="cadastre-buildings">
              <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Buildings on Parcel</div>
              <div className="space-y-1">
                {buildings.map((b, i) => {
                  const age = parseBuildingAge(b.construction_era);
                  return (
                    <div key={i} className="flex items-center gap-1.5 flex-wrap" data-testid={`building-${i}`}>
                      <Tip tip={`Building #${b.number}: ${b.destination || 'Unknown use'}. Total surface ${b.surface_m2 || '?'} m². ${b.construction_era || ''}. ${b.floors_above ? b.floors_above + ' floors above' : ''} ${b.floors_below ? '+ ' + b.floors_below + ' below' : ''}`} source={meta.buildings}>
                        <div className="flex items-center gap-1.5 text-[8px]">
                          <Buildings weight="bold" className="w-3 h-3 text-gray-400 shrink-0" />
                          <span className="font-semibold text-gray-700">#{b.number}</span>
                          <span className="text-gray-500">{b.destination}</span>
                          {b.surface_m2 && <span className="text-gray-400 tabular-nums">{fmt(b.surface_m2)} m²</span>}
                        </div>
                      </Tip>
                      {age && (
                        <Tip tip={`Built ${b.construction_era}. Estimated age: ~${age.age}+ years (${age.label}).`} source="SITG construction era">
                          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded tabular-nums ${
                            age.age >= 50 ? 'bg-red-50 text-red-700 border border-red-200' :
                            age.age >= 30 ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                            'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`} data-testid={`building-age-${i}`}>
                            ~{age.age} yrs
                          </span>
                        </Tip>
                      )}
                      {b.construction_era && !age && (
                        <span className="text-[7px] text-gray-400">{b.construction_era}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── 4. RESTRICTIONS & INSIGHTS ── */}
          {(forestRestrictions.length > 0 || noiseRestrictions.length > 0 || setbackRestrictions.length > 0 || otherRestrictions.length > 0) && (
            <div data-testid="cadastre-restrictions">
              <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Restrictions & Insights</div>
              <div className="space-y-0.5">
                {/* Forest */}
                {forestRestrictions.map((r, i) => (
                  <Tip
                    key={`f${i}`}
                    tip={r.area_m2 > 0
                      ? `${r.label}: ${fmt(r.area_m2)} m² (${r.percent}%). ${r.legal_refs?.[0]?.title || ''}`
                      : `${r.label}. ${r.legal_refs?.[0]?.title || ''}`}
                    source={r.theme}
                  >
                    <div className="flex items-center gap-1 text-[8px]" data-testid={`restriction-forest-${i}`}>
                      <TreeEvergreen weight="fill" className="w-3 h-3 text-green-600 shrink-0" />
                      <span className="text-green-800 font-medium">{r.label}</span>
                      {r.area_m2 > 0 && (
                        <span className="text-green-500 text-[7px]">{fmt(r.area_m2)} m² ({r.percent}%)</span>
                      )}
                    </div>
                  </Tip>
                ))}

                {/* Noise */}
                {noiseRestrictions.map((r, i) => (
                  <Tip
                    key={`n${i}`}
                    tip={r.percent > 0
                      ? `${r.label}: affects ${r.percent}% of parcel. Higher levels = stricter noise limits.`
                      : `${r.label}. Higher levels = stricter noise limits.`}
                    source="RDPPF degré sensibilité bruit"
                  >
                    <div className="flex items-center gap-1 text-[8px]" data-testid={`restriction-noise-${i}`}>
                      <SpeakerHigh weight="bold" className="w-3 h-3 text-violet-500 shrink-0" />
                      <span className="text-violet-700 font-medium">{r.label}</span>
                      {r.percent > 0 && (
                        <span className="text-violet-400 text-[7px]">{r.percent}%</span>
                      )}
                    </div>
                  </Tip>
                ))}

                {/* Setbacks */}
                {setbackRestrictions.map((r, i) => (
                  <Tip
                    key={`s${i}`}
                    tip={r.area_m2 > 0
                      ? `${r.label}: ${fmt(r.area_m2)} m² affected (${r.percent}%). Building setback from roads.`
                      : `${r.label}. Building setback from roads.`}
                    source="RDPPF alignements construction"
                  >
                    <div className="flex items-center gap-1 text-[8px]" data-testid={`restriction-setback-${i}`}>
                      <Warning weight="bold" className="w-3 h-3 text-amber-500 shrink-0" />
                      <span className="text-amber-700 font-medium">{r.label}</span>
                      {r.area_m2 > 0 && (
                        <span className="text-amber-400 text-[7px]">{fmt(r.area_m2)} m² ({r.percent}%)</span>
                      )}
                    </div>
                  </Tip>
                ))}

                {/* Other */}
                {otherRestrictions.map((r, i) => (
                  <Tip
                    key={`o${i}`}
                    tip={r.area_m2 > 0 ? `${r.label}: ${fmt(r.area_m2)} m² (${r.percent}%).` : r.label}
                    source={r.theme}
                  >
                    <div className="flex items-center gap-1 text-[8px]" data-testid={`restriction-other-${i}`}>
                      <Info weight="bold" className="w-3 h-3 text-gray-400 shrink-0" />
                      <span className="text-gray-600 font-medium">{r.label}</span>
                      {r.area_m2 > 0 && (
                        <span className="text-gray-400 text-[7px]">{fmt(r.area_m2)} m² ({r.percent}%)</span>
                      )}
                    </div>
                  </Tip>
                ))}
              </div>
            </div>
          )}

          {/* Positive signals (not concerned) — show top 3 */}
          {clearThemes.length > 0 && (
            <div data-testid="cadastre-clear">
              <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Not Concerned By</div>
              <div className="flex flex-wrap gap-1">
                {clearThemes.slice(0, 5).map((t, i) => (
                  <Tip key={i} tip={`This parcel is NOT affected by: ${t}. Positive signal — no restriction applies.`} source="RDPPF not concerned themes">
                    <span className="text-[7px] px-1.5 py-0.5 rounded bg-green-50 text-green-600 border border-green-100 flex items-center gap-0.5" data-testid={`clear-theme-${i}`}>
                      <ShieldCheck weight="bold" className="w-2.5 h-2.5" />
                      {t.replace(/Plans d'affectation \(cantonaux\/communaux\): /, '').substring(0, 35)}
                    </span>
                  </Tip>
                ))}
                {clearThemes.length > 5 && (
                  <span className="text-[7px] text-gray-400 py-0.5">+{clearThemes.length - 5} more</span>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
